import React, { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { usePanelAuth, formatApiError } from './PanelAuthContext';

/**
 * Reusable login page used by both Admin and Employee portals.
 * Props:
 *  - role: 'admin' | 'employee'
 *  - title, subtitle, accent (tailwind gradient classes), iconColor
 *  - redirectTo: route to navigate after successful login
 */
function PanelLoginInner({ role, title, subtitle, redirectTo, accent, iconColor }) {
  const nav = useNavigate();
  const { gwToken, panelLogin, user, bootChecked } = usePanelAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (bootChecked && !gwToken) return <Navigate to="/official" replace />;
  // Admin portal also accepts super_admin role
  const allowedRoles = role === 'admin' ? ['admin', 'super_admin'] : [role];
  if (bootChecked && user && allowedRoles.includes(user.role)) return <Navigate to={redirectTo} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await panelLogin(identifier.trim(), password, remember);
      if (!allowedRoles.includes(res.user.role)) {
        setError(`This portal is for ${role}s only. Your account is registered as ${res.user.role}.`);
        return;
      }
      nav(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${accent}`}>
      <div className="w-full max-w-md">
        <Link to="/official/portal-selection" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4">
          <ArrowLeft size={14} /> Back to portal selection
        </Link>

        <div className="text-center mb-6 text-white">
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${iconColor} mb-3`}>
            <ShieldCheck size={26} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">{title}</h1>
          <p className="mt-2 text-sm text-white/70">{subtitle}</p>
        </div>

        <form onSubmit={submit} className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-6 sm:p-7 border border-white/20" data-testid={`${role}-login-form`}>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Email or Mobile</Label>
              <Input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@entorid.com or 9876543210"
                className="mt-1 h-11"
                required
                autoComplete="username"
                data-testid={`${role}-identifier`}
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Password</Label>
              <div className="relative mt-1">
                <Input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-10"
                  required
                  autoComplete="current-password"
                  data-testid={`${role}-password`}
                />
                <button type="button" onClick={() => setShow(s => !s)} className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4" />
                <span>Remember me</span>
              </label>
              <button type="button" onClick={() => alert(role === 'admin' ? 'Use the gateway password to recover. Contact super-admin if locked out.' : 'Contact your admin to reset password.')} className="text-emerald-700 hover:underline">
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2" data-testid={`${role}-login-error`}>
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !identifier || !password}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl"
              data-testid={`${role}-login-submit`}
            >
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Signing in…</> : `Sign in to ${role === 'admin' ? 'Admin' : 'Employee'} Portal`}
            </Button>
          </div>

          <p className="mt-5 text-[11px] text-center text-slate-500">
            🔒 5 failed attempts = 30-minute lock. All actions audited.
          </p>
        </form>
      </div>
    </div>
  );
}

export function AdminLogin() {
  return (
    <PanelLoginInner
      role="admin"
      title="Admin Portal Login"
      subtitle="Master control · full access"
      redirectTo="/official/admin"
      accent="bg-gradient-to-br from-slate-900 via-emerald-950 to-emerald-800"
      iconColor="bg-emerald-500/20 ring-2 ring-emerald-400/40 text-emerald-300"
    />
  );
}

export function EmployeeLogin() {
  return (
    <PanelLoginInner
      role="employee"
      title="Employee Portal Login"
      subtitle="View assigned jobs · update status"
      redirectTo="/official/employee"
      accent="bg-gradient-to-br from-slate-900 via-blue-950 to-blue-800"
      iconColor="bg-blue-500/20 ring-2 ring-blue-400/40 text-blue-300"
    />
  );
}
